import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusableIn(container: HTMLElement): HTMLElement[] {
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement
    );
}

// Moves focus into the dialog on open, keeps Tab inside it, and hands focus back on close.
export function useDialogFocus(
    dialogRef: RefObject<HTMLElement>,
    initialFocusRef?: RefObject<HTMLElement>
): void {
    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const previouslyFocused =
            document.activeElement instanceof HTMLElement ? document.activeElement : null;
        (initialFocusRef?.current ?? focusableIn(dialog)[0] ?? dialog).focus();

        const handleTab = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;
            const focusable = focusableIn(dialog);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        dialog.addEventListener('keydown', handleTab);
        return () => {
            dialog.removeEventListener('keydown', handleTab);
            previouslyFocused?.focus();
        };
    }, [dialogRef, initialFocusRef]);
}
